/**
 * Display helpers for patron vs member vs visitor identifiers.
 * IGT venues: club-facing "Member #" (membership_number) vs internal "Player ID" (member_id).
 */

export const ID_LABELS = {
  patron: 'Patron ID',
  membership: 'Member #',
  player: 'Player ID',
  visitor: 'Visitor ID',
}

function str(v) {
  if (v == null) return ''
  return String(v).trim()
}

/** Club membership number for staff display and search. */
export function displayMembershipNumber(row = {}) {
  const mn = str(
    row.membership_number ??
      row.membershipNumber ??
      row.MembershipNumber ??
      row.staffMemberNumber
  )
  if (mn) return mn
  const mid = str(row.member_id ?? row.memberId ?? row.MID)
  const pid = displayPlayerId(row)
  // Legacy payout rows: club # was stored in member_id before migration 203
  if (mid && (!pid || mid === pid)) return mid
  if (mid && !str(row.membership_number ?? row.membershipNumber)) return mid
  return null
}

/** IGT player id (gaming sessions) — only when distinct from membership #. */
export function displayPlayerId(row = {}) {
  const explicit = str(row.player_id ?? row.playerId ?? row.PlayerID)
  if (explicit) return explicit
  const mn = str(row.membership_number ?? row.membershipNumber ?? row.MembershipNumber)
  const mid = str(row.member_id ?? row.memberId ?? row.MID)
  if (mn && mid && mid !== mn) return mid
  if (!mn && mid && !/^visitor_/i.test(mid)) return mid
  return null
}

export function resolvePatronIdentity(row = {}) {
  const masterProfileId = str(row.master_profile_id || row.patron_id) || null
  const visitorId = str(row.visitor_id || row.patron_visitor_id) || null
  const profileType = str(row.profile_type || row.patron_profile_type).toUpperCase() || null
  const membershipNumber = displayMembershipNumber(row)
  const playerId = displayPlayerId(row)
  const rawMemberId = str(row.member_id)
  const isLegacyVisitor = /^visitor_/i.test(rawMemberId)
  const legacyVisitorRef = isLegacyVisitor ? rawMemberId.replace(/^visitor_/i, '') : null
  const resolvedVisitorId = visitorId || legacyVisitorRef || null

  let subjectType = 'patron'
  if (profileType === 'VISITOR' || isLegacyVisitor || (resolvedVisitorId && !membershipNumber && !playerId)) {
    subjectType = 'visitor'
  } else if (profileType === 'MEMBER' || membershipNumber || playerId) {
    subjectType = 'member'
  }

  const patronId = masterProfileId || membershipNumber || playerId || resolvedVisitorId || null

  const profileSafeId = str(row.profilesafe_profile_id || row.profile_safe_profile_id) || null

  return {
    patron_id: patronId,
    master_profile_id: masterProfileId,
    profilesafe_profile_id: profileSafeId,
    subject_type: subjectType,
    membership_number: membershipNumber,
    player_id: playerId,
    /** @deprecated Prefer membership_number for staff display */
    member_id: membershipNumber || playerId,
    visitor_id: resolvedVisitorId,
    member_name: row.member_name ? str(row.member_name) : null,
  }
}

export function subjectTypeLabel(subjectType) {
  if (subjectType === 'member') return 'Member'
  if (subjectType === 'visitor') return 'Visitor'
  return 'Patron'
}

export function subjectTypeBadgeClass(subjectType) {
  if (subjectType === 'member') return 'bg-blue-100 text-blue-800'
  if (subjectType === 'visitor') return 'bg-violet-100 text-violet-800'
  return 'bg-slate-100 text-slate-700'
}

/** One-line patron id for investigation / ECDD list cards. */
export function formatPatronListIdLine(row = {}) {
  const id = resolvePatronIdentity(row)
  if (!id.patron_id) return ''
  const secondary = clubOrVisitorIdLine(id)
  if (secondary) return `${id.patron_id} · ${secondary.label} ${secondary.value}`
  return id.patron_id
}

/** Secondary club/VMS id line for list cells. */
export function clubOrVisitorIdLine(identity) {
  if (identity.subject_type === 'member' && identity.membership_number) {
    return { label: ID_LABELS.membership, value: identity.membership_number }
  }
  if (identity.subject_type === 'member' && identity.player_id) {
    return { label: ID_LABELS.player, value: identity.player_id }
  }
  if (identity.subject_type === 'visitor' && identity.visitor_id) {
    return { label: ID_LABELS.visitor, value: identity.visitor_id }
  }
  if (identity.membership_number) return { label: ID_LABELS.membership, value: identity.membership_number }
  if (identity.player_id) return { label: ID_LABELS.player, value: identity.player_id }
  if (identity.visitor_id) return { label: ID_LABELS.visitor, value: identity.visitor_id }
  return null
}

/** Staff-facing member key for API calls (watchlist, payout history, compliance). */
export function staffMemberLookupKey(row = {}) {
  return displayMembershipNumber(row) || displayPlayerId(row) || null
}
